import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';
import { Button } from './ui/Button';
export function PWAInstallPrompt() {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showPrompt, setShowPrompt] = useState(false);
    const [isInstalled, setIsInstalled] = useState(false);
    useEffect(() => {
        // Check if already installed
        const isInStandaloneMode = ('standalone' in window.navigator && window.navigator.standalone) ||
            window.matchMedia('(display-mode: standalone)').matches;
        if (isInStandaloneMode) {
            setIsInstalled(true);
            return;
        }
        // Listen for install prompt event
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            const event = e;
            setDeferredPrompt(event);
            // Check if user has dismissed before
            const dismissed = localStorage.getItem('pwa-install-dismissed');
            const dismissedTime = dismissed ? parseInt(dismissed) : 0;
            const now = Date.now();
            // Show prompt if not dismissed in the last 7 days
            if (!dismissed || now - dismissedTime > 7 * 24 * 60 * 60 * 1000) {
                setShowPrompt(true);
            }
        };
        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        // Listen for successful install
        window.addEventListener('appinstalled', () => {
            console.log('[PWA] App installed successfully');
            setIsInstalled(true);
            setShowPrompt(false);
            setDeferredPrompt(null);
            localStorage.removeItem('pwa-install-dismissed');
        });
        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);
    const handleInstallClick = async () => {
        if (!deferredPrompt) {
            return;
        }
        // Show the install prompt
        await deferredPrompt.prompt();
        // Wait for the user to respond to the prompt
        const choiceResult = await deferredPrompt.userChoice;
        console.log(`[PWA] User response: ${choiceResult.outcome}`);
        if (choiceResult.outcome === 'accepted') {
            console.log('[PWA] User accepted the install prompt');
        }
        else {
            console.log('[PWA] User dismissed the install prompt');
        }
        setDeferredPrompt(null);
        setShowPrompt(false);
    };
    const handleDismiss = () => {
        setShowPrompt(false);
        localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    };
    if (isInstalled || !showPrompt) {
        return null;
    }
    return (_jsxs("div", { className: "fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50 rounded-lg shadow-2xl p-4 animate-slide-up", style: {
            backgroundColor: 'var(--bg-elevated)',
            border: '1px solid var(--border-color)',
        }, children: [_jsxs("div", { className: "flex items-start gap-3", children: [_jsx("div", { className: "flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center", style: { backgroundColor: '#8B5CF6' }, children: _jsx(Download, { className: "w-6 h-6 text-white" }) }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("h3", { className: "text-lg font-semibold mb-1", style: { color: 'var(--text-primary)' }, children: "Install COW Platform" }), _jsx("p", { className: "text-sm mb-3", style: { color: 'var(--text-secondary)' }, children: "Install the app for a faster experience with offline support and quick access." }), _jsxs("div", { className: "flex gap-2", children: [_jsx(Button, { onClick: handleInstallClick, size: "sm", style: {
                                            backgroundColor: '#8B5CF6',
                                            color: 'white',
                                        }, children: "Install" }), _jsx(Button, { onClick: handleDismiss, size: "sm", variant: "outline", style: {
                                            color: 'var(--text-secondary)',
                                        }, children: "Not now" })] })] }), _jsx("button", { onClick: handleDismiss, className: "flex-shrink-0 p-1 rounded hover:opacity-70 transition-opacity", style: { color: 'var(--text-tertiary)' }, "aria-label": "Close install prompt", children: _jsx(X, { className: "w-5 h-5" }) })] }), _jsx("style", { children: `
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      ` })] }));
}
